import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ApplicationHostTypeService,
  EntityResponseDto,
  ReferenceDataConstants,
  ReferenceDataLabels,
  ReferenceDataOptionLists,
  ReferenceDataService,
  RomeNotificationService,
  ValidationErrors
} from 'rome-library';

import { States } from '../../../shared';
import { TaggingService } from '../../../tag-manager';
import { RESET_PASSWORD_GUID } from '../../constants/route-params-name';
import { ForgottenPasswordResetRequestDto } from '../../models/forgotten-password-reset-request-dto.model';
import { UserService } from '../../services/user.service';
import { UserCredentialStore } from '../../../interfaceTypes/sharedFeatures/registration/services';

interface ChallengePasswordResetResponse {
  QuestionCode?: string;
  IsPhoneValidated?: boolean;
  MaskedPhoneNumber?: string;
  HasExceededMaximumOtpSendLimit?: boolean;
  HasAnOtpCodeActive?: boolean;
  HasUserExceededMaximumOtpValidation?: boolean;
  IsValidOtp?: boolean;
  HasOtpNotBeenSent?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'boi-reset-forgotten-password',
  templateUrl: './reset-forgotten-password.page.html'
})
export class ResetForgottenPasswordPage implements OnInit {
  public readonly referenceDataOptionLists = ReferenceDataOptionLists;
  public readonly referenceDataLabels = ReferenceDataLabels;
  public readonly referenceDataConstants = ReferenceDataConstants;
  public forgottenPasswordResetRequestDto = new ForgottenPasswordResetRequestDto();
  public passwordResetPhoneValidated: ChallengePasswordResetResponse;
  public validationErrors: ValidationErrors;
  public errorMessage: string;
  public isAnswerChecked: boolean;
  public statusCode = undefined;
  public isOtpSend: boolean;
  public otpCode: string = '';
  public isOtpInvalid: boolean;
  public blockResend: boolean;

  constructor(
    private readonly userService: UserService,
    private readonly activeRoute: ActivatedRoute,
    private readonly notificationService: RomeNotificationService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly applicationHostTypeService: ApplicationHostTypeService,
    private readonly router: Router,
    private readonly taggingService: TaggingService,
    private readonly userCredentialStore: UserCredentialStore
  ) {}

  ngOnInit(): void {
    this.taggingService.resetDataLayer();
    this.taggingService.execute();

    this.forgottenPasswordResetRequestDto.RequestGuid = this.activeRoute.snapshot.paramMap.get(RESET_PASSWORD_GUID);

    this.userService
      .getSecurityChallengeForPasswordReset(this.forgottenPasswordResetRequestDto.RequestGuid)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response: ChallengePasswordResetResponse) => {
          if (response?.IsPhoneValidated) {
            this.passwordResetPhoneValidated = response;

            if (response?.HasAnOtpCodeActive) {
              this.isOtpSend = true;
            }
          } else {
            this.forgottenPasswordResetRequestDto.QuestionCode = response.QuestionCode;
          }
        },
        (error) => {
          this.errorMessage = JSON.parse(error.error.Message);
        }
      );
  }

  public resetForgottenPassword(form: NgForm): void {
    if (form.valid) {
      this.userService
        .resetForgottenPasswordRequest(this.forgottenPasswordResetRequestDto)
        .pipe(untilDestroyed(this))
        .subscribe(
          (response) => {
            this.handleResponse(response);
          },
          (errorResponse) => {
            this.handleErrorResponse(errorResponse);
          }
        );
    } else {
      form.control.markAllAsTouched();
    }
  }

  private handleResponse(response: EntityResponseDto<string>): void {
    this.validationErrors = response.ValidationErrors;
    this.validationErrors.BusinessValidationErrors = this.validationErrors.FullValidationErrors;
    if (this.validationErrors.FullValidationErrors.length <= 1) {
      const message = this.referenceDataService.getLocalizeLabel(this.referenceDataLabels.NotificationSaveSuccess);
      this.notificationService.showInfo(message);

      this.userCredentialStore.setExpiredPassword(undefined);
      this.router.navigate([this.applicationHostTypeService.getCode(), States.Login]);
    }
  }

  private handleErrorResponse(errorResponse: any): void {
    this.errorMessage = errorResponse.error.Message;

    if (
      this.errorMessage ===
      this.referenceDataService.getLocalizeLabel(this.referenceDataLabels.InvalidResetForgottenPasswordRequest)
    ) {
      this.goToLogin(this.referenceDataLabels.InvalidResetForgottenPasswordRequest);
    }
  }

  public goToLogin(isEmailUpdateConfirmed?: any): void {
    void this.router.navigate([this.applicationHostTypeService.getCode(), States.Login, isEmailUpdateConfirmed]);
  }

  public checkAnswer(form: NgForm): void {
    this.userService
      .checkSecurityQuestionAnswerForPasswordReset(this.forgottenPasswordResetRequestDto)
      .pipe(untilDestroyed(this))
      .subscribe(
        (response) => {
          this.isAnswerChecked = response;
          this.statusCode = response ? undefined : this.referenceDataLabels.WrongAnswer;
          const answer = this.forgottenPasswordResetRequestDto.Answer;
          form.resetForm();
          this.forgottenPasswordResetRequestDto.Answer = answer;
        },
        (errorResponse) => {
          this.errorMessage = JSON.parse(errorResponse.error.Message);
        }
      );
  }

  public sendCode(): void {
    this.userService
      .sendOtpCodeForPasswordResetRequest(this.forgottenPasswordResetRequestDto.RequestGuid)
      .subscribe(
        (response: {
          HasExceededMaximumOtpSendLimit: boolean;
          HasOtpNotBeenSent: boolean;
          HasAnOtpCodeActive: boolean;
          HasUserExceededMaximumOtpValidation: boolean;
        }) => {
          this.passwordResetPhoneValidated.HasExceededMaximumOtpSendLimit = response.HasExceededMaximumOtpSendLimit;

          this.isOtpSend = true;
          this.isOtpInvalid = false;
          this.passwordResetPhoneValidated.HasOtpNotBeenSent = response.HasOtpNotBeenSent;
          this.passwordResetPhoneValidated.HasAnOtpCodeActive = response.HasAnOtpCodeActive;
          this.passwordResetPhoneValidated.HasUserExceededMaximumOtpValidation =
            response.HasUserExceededMaximumOtpValidation;

          if (response.HasOtpNotBeenSent) {
            this.blockResend = true;
          }
        }
      );
  }

  public validateOtpCode(form: NgForm): void {
    if (this.otpCode) {
      this.userService
        .validateOtpCode({
          OtpCode: this.otpCode,
          SessionID: this.forgottenPasswordResetRequestDto.RequestGuid
        })
        .subscribe((response: ChallengePasswordResetResponse) => {
          if (response.IsValidOtp) {
            this.isAnswerChecked = true;
            this.forgottenPasswordResetRequestDto.Answer = this.otpCode;
            this.isOtpInvalid = false;
            form.resetForm();
          } else {
            this.passwordResetPhoneValidated.HasAnOtpCodeActive = response.HasAnOtpCodeActive;
            this.passwordResetPhoneValidated.HasUserExceededMaximumOtpValidation =
              response.HasUserExceededMaximumOtpValidation;
            this.passwordResetPhoneValidated.HasOtpNotBeenSent = false;

            if (response.HasUserExceededMaximumOtpValidation) {
              this.isOtpInvalid = false;
            } else {
              this.isOtpInvalid = true;
            }

            form.resetForm();
          }
        });
    }
  }
}
